import { useEffect, useMemo, useRef, useState } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { SITE_ROUTES } from "@/config/siteRoutes";
import { getVenueXPreviewPayload } from "@/data/previewContent";
import {
  DEFAULT_BLOG_JOURNAL,
  fetchBlogPost,
  type BlogPostDetail,
} from "@/pages/BlogPage/blogApi";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";
import { getSiteLocale, localizeHref } from "@/i18n/locale";
import { localizeContent, uiText } from "@/i18n/translations";

import styles from "./BlogArticlePage.module.scss";

type BlogArticlePageProps = {
  slug: string;
};

type ArticleSection = {
  id: string;
  title: string;
  html: string;
};

function resolveActiveSectionId(
  sectionPositions: Array<{ id: string; top: number; bottom: number }>,
  activationLine: number,
) {
  const sectionAtReadingLine = sectionPositions.find(
    (section) => section.top <= activationLine && section.bottom > activationLine,
  );
  if (sectionAtReadingLine) return sectionAtReadingLine.id;

  let activeId = sectionPositions[0]?.id || "";

  for (const section of sectionPositions) {
    if (section.top > activationLine) break;
    activeId = section.id;
  }

  return activeId;
}

function serializeNodes(articleDocument: Document, nodes: Node[]) {
  const wrapper = articleDocument.createElement("div");
  nodes.forEach((node) => wrapper.appendChild(node.cloneNode(true)));
  return wrapper.innerHTML;
}

function prepareArticleBody(bodyHtml: string) {
  if (!bodyHtml || typeof DOMParser === "undefined") {
    return { introHtml: bodyHtml, sections: [] as ArticleSection[] };
  }

  const articleDocument = new DOMParser().parseFromString(bodyHtml, "text/html");
  const usedIds = new Set<string>();
  const introNodes: Node[] = [];
  const sectionDrafts: Array<ArticleSection & { nodes: Node[] }> = [];
  let currentSection: (ArticleSection & { nodes: Node[] }) | null = null;

  Array.from(articleDocument.body.childNodes).forEach((node) => {
    const isSectionHeading =
      node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).tagName.toLowerCase() === "h2";

    if (isSectionHeading) {
      const heading = node as HTMLHeadingElement;
      const sectionIndex = sectionDrafts.length;
      const title = heading.textContent?.trim() || `Section ${sectionIndex + 1}`;
      const baseId = title
        .toLocaleLowerCase("en")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "") || `section-${sectionIndex + 1}`;
      let id = baseId;
      let suffix = 2;

      while (usedIds.has(id)) {
        id = `${baseId}-${suffix}`;
        suffix += 1;
      }

      usedIds.add(id);
      heading.id = id;
      currentSection = { id, title, html: "", nodes: [node] };
      sectionDrafts.push(currentSection);
      return;
    }

    if (currentSection) {
      currentSection.nodes.push(node);
    } else {
      introNodes.push(node);
    }
  });

  return {
    introHtml: serializeNodes(articleDocument, introNodes),
    sections: sectionDrafts.map(({ nodes, ...section }) => ({
      ...section,
      html: serializeNodes(articleDocument, nodes),
    })),
  };
}

export function BlogArticlePage({ slug }: BlogArticlePageProps) {
  const locale = getSiteLocale();
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const previewContent = getVenueXPreviewPayload<BlogPostDetail>("blog-detail");
  const [post, setPost] = useState<BlogPostDetail | null>(() => localizeContent(previewContent, locale));
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const [readingProgress, setReadingProgress] = useState(0);
  const journal = post?.journal || localizeContent(DEFAULT_BLOG_JOURNAL, locale);
  const content = journal.presentation;
  const articleBodyRef = useRef<HTMLDivElement>(null);
  const guideRailRef = useRef<HTMLElement>(null);
  const guideNavRef = useRef<HTMLElement>(null);
  const discoveryRailRef = useRef<HTMLElement>(null);
  const discoveryInnerRef = useRef<HTMLDivElement>(null);
  const preparedArticle = useMemo(
    () => prepareArticleBody(post?.bodyHtml || ""),
    [post?.bodyHtml],
  );

  useEffect(() => {
    if (previewContent) {
      document.title = /venuex/i.test(previewContent.seoTitle)
        ? previewContent.seoTitle
        : `${previewContent.seoTitle} | VenueX Journal`;
      document
        .querySelector<HTMLMetaElement>('meta[name="description"]')
        ?.setAttribute("content", previewContent.seoDescription);
      return;
    }

    const controller = new AbortController();
    setPost(null);
    setError("");
    fetchBlogPost(slug, controller.signal)
      .then((article) => {
        setPost(article);
        document.title = /venuex/i.test(article.seoTitle)
          ? article.seoTitle
          : `${article.seoTitle} | VenueX Journal`;
        const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        description?.setAttribute("content", article.seoDescription);
      })
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(reason instanceof Error ? reason.message : uiText("The article could not be loaded.", locale));
      });
    return () => controller.abort();
  }, [previewContent, slug]);

  useEffect(() => {
    const articleBody = articleBodyRef.current;
    if (!articleBody || !preparedArticle.sections.length) return;

    const articleSections = Array.from(
      articleBody.querySelectorAll<HTMLElement>("[data-article-section]"),
    );
    if (!articleSections.length) return;

    let frameId: number | null = null;
    let lastArticleTop = Number.NaN;
    const updateActiveSection = () => {
      const activationLine = Math.min(window.innerHeight * 0.36, 340);
      const sectionPositions = articleSections.map((section) => {
        const rect = section.getBoundingClientRect();
        return {
          id: section.dataset.articleSection || "",
          top: rect.top,
          bottom: rect.bottom,
        };
      });
      const nextActiveSection = resolveActiveSectionId(
        sectionPositions,
        activationLine,
      );

      setActiveSection((current) =>
        current === nextActiveSection ? current : nextActiveSection,
      );
    };
    const monitorArticlePosition = () => {
      const articleTop = articleBody.getBoundingClientRect().top;
      if (!Number.isFinite(lastArticleTop) || Math.abs(articleTop - lastArticleTop) > 0.25) {
        lastArticleTop = articleTop;
        updateActiveSection();
      }
      frameId = window.requestAnimationFrame(monitorArticlePosition);
    };

    updateActiveSection();
    frameId = window.requestAnimationFrame(monitorArticlePosition);
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [preparedArticle]);

  useEffect(() => {
    const discoveryRail = discoveryRailRef.current;
    const discoveryInner = discoveryInnerRef.current;
    if (!discoveryRail || !discoveryInner) return;

    let frameId: number | null = null;
    const clearPosition = () => {
      discoveryInner.style.position = "relative";
      discoveryInner.style.top = "auto";
      discoveryInner.style.right = "auto";
      discoveryInner.style.bottom = "auto";
      discoveryInner.style.left = "auto";
      discoveryInner.style.width = "auto";
    };
    const updateDiscoveryPosition = () => {
      if (window.matchMedia("(max-width: 1279px)").matches) {
        clearPosition();
        frameId = null;
        return;
      }

      const fixedTop = 80;
      const railRect = discoveryRail.getBoundingClientRect();
      const railTop = railRect.top + window.scrollY;
      const railBottom = railTop + discoveryRail.offsetHeight;
      const panelHeight = discoveryInner.offsetHeight;
      const viewportPanelTop = window.scrollY + fixedTop;

      if (viewportPanelTop <= railTop) {
        clearPosition();
      } else if (viewportPanelTop + panelHeight >= railBottom) {
        discoveryInner.style.position = "absolute";
        discoveryInner.style.top = "auto";
        discoveryInner.style.right = "0";
        discoveryInner.style.bottom = "0";
        discoveryInner.style.left = "0";
        discoveryInner.style.width = "auto";
      } else {
        discoveryInner.style.position = "fixed";
        discoveryInner.style.top = `${fixedTop}px`;
        discoveryInner.style.right = "auto";
        discoveryInner.style.bottom = "auto";
        discoveryInner.style.left = `${railRect.left}px`;
        discoveryInner.style.width = `${railRect.width}px`;
      }

      frameId = null;
    };
    const scheduleUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateDiscoveryPosition);
    };

    updateDiscoveryPosition();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      clearPosition();
    };
  }, [preparedArticle]);

  useEffect(() => {
    const guideRail = guideRailRef.current;
    const guideNav = guideNavRef.current;
    if (!guideRail || !guideNav || !preparedArticle.sections.length) return;

    let frameId: number | null = null;
    const clearPosition = () => {
      guideNav.style.position = "relative";
      guideNav.style.top = "auto";
      guideNav.style.right = "auto";
      guideNav.style.bottom = "auto";
      guideNav.style.left = "auto";
      guideNav.style.width = "auto";
    };
    const updateGuidePosition = () => {
      if (window.matchMedia("(max-width: 991px)").matches) {
        clearPosition();
        frameId = null;
        return;
      }

      const fixedTop = 188;
      const railRect = guideRail.getBoundingClientRect();
      const railTop = railRect.top + window.scrollY;
      const railBottom = railTop + guideRail.offsetHeight;
      const guideHeight = guideNav.offsetHeight;
      const viewportGuideTop = window.scrollY + fixedTop;

      if (viewportGuideTop <= railTop) {
        clearPosition();
      } else if (viewportGuideTop + guideHeight >= railBottom) {
        guideNav.style.position = "absolute";
        guideNav.style.top = "auto";
        guideNav.style.right = "0";
        guideNav.style.bottom = "0";
        guideNav.style.left = "0";
        guideNav.style.width = "auto";
      } else {
        guideNav.style.position = "fixed";
        guideNav.style.top = `${fixedTop}px`;
        guideNav.style.right = "auto";
        guideNav.style.bottom = "auto";
        guideNav.style.left = `${railRect.left}px`;
        guideNav.style.width = `${railRect.width}px`;
      }

      frameId = null;
    };
    const scheduleGuideUpdate = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(updateGuidePosition);
    };

    updateGuidePosition();
    window.addEventListener("scroll", scheduleGuideUpdate, { passive: true });
    window.addEventListener("resize", scheduleGuideUpdate);
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", scheduleGuideUpdate);
      window.removeEventListener("resize", scheduleGuideUpdate);
      clearPosition();
    };
  }, [preparedArticle]);

  useEffect(() => {
    const articleBody = articleBodyRef.current;
    if (!articleBody) return;

    let frameId: number | null = null;
    let lastArticleTop = Number.NaN;
    const updateProgress = () => {
      const articleRect = articleBody.getBoundingClientRect();
      const readingDistance = Math.max(articleBody.offsetHeight - window.innerHeight * .55, 1);
      const progress = ((window.innerHeight * .28 - articleRect.top) / readingDistance) * 100;
      setReadingProgress(Math.round(Math.min(100, Math.max(0, progress))));
    };
    const monitorArticlePosition = () => {
      const articleTop = articleBody.getBoundingClientRect().top;
      if (!Number.isFinite(lastArticleTop) || Math.abs(articleTop - lastArticleTop) > 0.25) {
        lastArticleTop = articleTop;
        updateProgress();
      }
      frameId = window.requestAnimationFrame(monitorArticlePosition);
    };

    updateProgress();
    frameId = window.requestAnimationFrame(monitorArticlePosition);
    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [preparedArticle]);

  if (error) {
    return (
      <InnerPageChrome currentPath={SITE_ROUTES.blog}>
        <section className={styles.statePage}>
          <span className={styles.meta}>{content.journal_label}</span>
          <h1>{content.article_unavailable_title}</h1>
          <p>{error}</p>
          <a href={localizeHref(SITE_ROUTES.blog, locale)}>{content.return_label}</a>
        </section>
      </InnerPageChrome>
    );
  }

  if (!post) {
    return (
      <InnerPageChrome currentPath={SITE_ROUTES.blog}>
        <section className={styles.loadingPage} aria-label={content.article_loading_label}>
          <span></span><span></span><span></span>
        </section>
      </InnerPageChrome>
    );
  }

  return (
    <InnerPageChrome currentPath={SITE_ROUTES.blog}>
      <article className={styles.page}>
        <section className={styles.hero} data-chrome-surface="dark">
          <div className={styles.heroImage}>
            <img src={post.heroImage} alt={post.heroImageAlt} />
          </div>
          <div className={styles.heroOverlay}></div>
          <div className="container-fluid">
            <div className={styles.heroContent}>
              <span className={styles.meta}>{post.category} / {post.date} / {post.readingTimeMinutes} {content.minutes_read_label}</span>
              <h1 className={styles.heroTitle}>{post.title}</h1>
              <p className={styles.heroLead}>{post.summary}</p>
            </div>
          </div>
        </section>

        <section className={styles.bodySection}>
          <div className={styles.articleContainer}>
            <aside ref={guideRailRef} className={styles.guideRail} aria-label={content.contents_label}>
              <nav ref={guideNavRef} className={styles.guideNav}>
                <span className={styles.railLabel}>{content.contents_label}</span>
                <ol>
                  {preparedArticle.sections.map((section) => (
                    <li key={section.id}>
                      <a
                        className={activeSection === section.id ? styles.activeGuideLink : undefined}
                        href={`#${section.id}`}
                        aria-current={activeSection === section.id ? "location" : undefined}
                      >
                        {section.title}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </aside>

            <main className={styles.articleMain}>
              {post.excerpt ? <div className={styles.bodyIntro}><p className={styles.dropcap}>{post.excerpt}</p></div> : null}
              <div className={styles.byline}>
                <span>{content.written_by_label} {post.authorName}</span>
                {post.tags.length ? <span>{post.tags.join(" · ")}</span> : null}
              </div>
              <div
                ref={articleBodyRef}
                className={styles.richText}
              >
                {preparedArticle.introHtml ? (
                  <div
                    className={styles.articlePrelude}
                    dangerouslySetInnerHTML={{ __html: preparedArticle.introHtml }}
                  />
                ) : null}
                {preparedArticle.sections.map((section) => (
                  <section
                    key={section.id}
                    className={styles.articleSection}
                    data-article-section={section.id}
                    dangerouslySetInnerHTML={{ __html: section.html }}
                  />
                ))}
              </div>
            </main>

            <aside ref={discoveryRailRef} className={styles.discoveryRail} aria-label={content.more_label}>
              <div ref={discoveryInnerRef} className={styles.discoveryInner}>
                <section className={styles.progressCard} aria-label={`${readingProgress}% of article read`}>
                  <div className={styles.progressTopline}>
                    <span className={styles.railLabel}>{content.progress_label}</span>
                    <strong>{readingProgress}%</strong>
                  </div>
                  <div className={styles.progressTrack} aria-hidden="true">
                    <span style={{ width: `${readingProgress}%` }}></span>
                  </div>
                  <dl className={styles.articleFacts}>
                    <div><dt>{content.time_label}</dt><dd>{post.readingTimeMinutes} {content.minutes_label}</dd></div>
                    <div><dt>{content.published_label}</dt><dd>{post.date}</dd></div>
                  </dl>
                </section>

                {post.relatedPosts.length ? (
                  <section className={styles.railSection}>
                    <span className={styles.railLabel}>{content.read_next_label}</span>
                    <div className={styles.railPosts}>
                      {post.relatedPosts.slice(0, 2).map((item) => (
                        <a key={item.slug} className={styles.railPost} href={localizeHref(item.url, locale)}>
                          <div className={styles.railPostImage}>
                            <img src={item.heroImage} alt={item.heroImageAlt} loading="lazy" />
                          </div>
                          <span>{item.category}</span>
                          <strong>{item.title}</strong>
                          <small>{item.readingTimeMinutes} {content.minutes_read_label} <span aria-hidden="true">→</span></small>
                        </a>
                      ))}
                    </div>
                  </section>
                ) : null}

                <section className={styles.exploreSection}>
                  <span className={styles.railLabel}>{content.explore_label}</span>
                  <div className={styles.visualLinks}>
                    {content.explore_links.map((link) => (
                      <a className={styles.visualLink} href={localizeHref(link.href, locale)} key={link.href}>
                        <img
                          src={link.image.image || link.image.fallback_url || ""}
                          alt={link.image.alt || ""}
                          loading="lazy"
                          style={{ objectPosition: link.image.position || "center center" }}
                        />
                        <span></span>
                        <strong>{link.title} <small>{link.meta}</small></strong>
                      </a>
                    ))}
                  </div>
                </section>

                <section className={styles.railCta}>
                  <span className={styles.railLabel}>{content.cta_kicker}</span>
                  <h2>{content.cta_title}</h2>
                  <p>{content.cta_body}</p>
                  <a href={localizeHref(content.cta_link.href, locale)}>{content.cta_link.label} <span aria-hidden="true">→</span></a>
                </section>

                <a className={styles.servicesLink} href={localizeHref(content.services_link.href, locale)}>
                  {content.services_link.label} <span aria-hidden="true">→</span>
                </a>
              </div>
            </aside>
          </div>
        </section>

        {post.relatedPosts.length ? (
          <section className={styles.relatedSection}>
            <div className="container">
              <div className={styles.relatedHeader}>
                <span className={styles.meta}>{content.related_eyebrow}</span>
                <h2 className={styles.relatedTitle}>{content.related_title}</h2>
              </div>
              <div className={styles.relatedGrid}>
                {post.relatedPosts.map((item) => (
                  <a key={item.slug} className={styles.relatedCard} href={localizeHref(item.url, locale)}>
                    <div className={styles.relatedImage}><img src={item.heroImage} alt={item.heroImageAlt} loading="lazy" /></div>
                    <div className={styles.relatedBody}>
                      <span className={styles.meta}>{item.category}</span>
                      <h3 className={styles.relatedCardTitle}>{item.title}</h3>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>
        ) : null}
      </article>
    </InnerPageChrome>
  );
}
